import { createProtanPolygonVisualizations } from './ProtanPolygonVisualizations';
import {
  HatchPatternConfig,
  HatchPatternType,
  generateHatchLines,
  createHatchShapes,
} from './utils/hatch-patterns';
import { MultiPolygon2 } from 'src/domain/geometry/geometric-types';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import {
  AbstractSheetFilledShape,
  SheetFillStyleSet,
} from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetFilledShape';
import { SheetShapeStyleType } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import {
  MultiPolygon2Fragment,
  SheetShapeDeepFragment,
  SheetScaleType,
  GetSparkelPropertiesForProjectQuery,
  CalibrationUnit,
} from 'src/gql/graphql';
import { ChakraColor, darkenColor } from 'src/utils/color-util';
import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;

type PolygonShapeType = {
  exterior: { points: THREE.Vector3[] };
  interiors: { points: THREE.Vector3[] }[];
};

export class PolygonShape extends AbstractSheetFilledShape {
  private protanShapes: {
    fireSectioningShapes: Autodesk.Edit2D.Shape[];
    cornerAreaShapes: Autodesk.Edit2D.Shape[];
    randAreaShapes: Autodesk.Edit2D.Shape[];
  } = {
    fireSectioningShapes: [],
    cornerAreaShapes: [],
    randAreaShapes: [],
  };

  /** Hatch pattern shapes rendered on top of the polygon */
  private hatchShapes: Autodesk.Edit2D.Shape[] = [];

  // eslint-disable-next-line complexity
  constructor(
    viewer: Viewer3D | GuiViewer3D,
    edit2d: Edit2D,
    activeSheetData: ActiveSheetData,
    shapeModel?: SheetShapeDeepFragment,
    polygons?: PolygonShapeType[],
    styleSet?: SheetFillStyleSet,
    isProtan?: boolean,
    externalShapes?: SheetShapeDeepFragment[],
    calibration?: SheetScaleType,
    sparkelProperties?: GetSparkelPropertiesForProjectQuery,
    hatchPattern?: HatchPatternConfig | null
  ) {
    super(viewer, edit2d, activeSheetData, shapeModel);
    const convertPoints = !!(shapeModel && shapeModel.sheetShapePolygon);
    const mappedPolygons =
      shapeModel && shapeModel.sheetShapePolygon
        ? shapeModel.sheetShapePolygon.multipolygon.polygons
        : polygons;

    if (shapeModel && !shapeModel.sheetShapePolygon) {
      throw new Error('shapeModel.sheetShapePolygon must be defined');
    } else if (!shapeModel && !Array.isArray(polygons)) {
      throw new Error('One shapeModel or polygons must be passed');
    } else if (!mappedPolygons || !Array.isArray(mappedPolygons)) {
      throw new Error('Cannot define polygons');
    }

    // Create the main polygon shapes
    this.shapes = mappedPolygons.map((polygon) => {
      const svg = this._toSVG(polygon, convertPoints);

      // https://aps.autodesk.com/en/docs/viewer/v7/developers_guide/advanced_options/edit2d-manual/#import-and-convert-svg-shapes
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return Autodesk.Edit2D.Shape.fromSVG(svg);
    });

    // Create hatch pattern shapes if pattern is provided
    if (hatchPattern && hatchPattern.type !== HatchPatternType.None) {
      // Use the polygon's fill color for hatch lines, darkened for better visibility
      const fillColor = styleSet?.default?.fillColor || '#000000';
      const hatchLineColor = darkenColor(fillColor, 0.8);
      this.createHatchPatternShapes(
        mappedPolygons,
        hatchPattern,
        convertPoints,
        hatchLineColor,
        calibration
      );
    }

    // Create Protan visualizations for Protan tenants
    if (isProtan && shapeModel && externalShapes && calibration) {
      this.createProtanVisualizations(
        shapeModel,
        externalShapes,
        calibration,
        sparkelProperties
      );
    }

    this.textLabelPoints = this.shapes.map((shape) => {
      // Ignored because Box2.center() is deprecated and there is no typed Box2.getCenter()
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return shape.getBBox().getCenter();
    });

    // For Protan tenants, make the main shape transparent to see overlaid visualizations
    if (isProtan && styleSet) {
      const protanStyleSet = { ...styleSet };
      if (protanStyleSet.default) {
        const usesFireSectioning =
          this.protanShapes.fireSectioningShapes.length > 0;
        protanStyleSet.default = {
          ...protanStyleSet.default,
          fillAlpha: usesFireSectioning ? 0 : 0.5, // Make main shape transparent
          fillColor: usesFireSectioning
            ? protanStyleSet.default.fillColor
            : ChakraColor.yellow300,
        };
        protanStyleSet.hovered = {
          ...protanStyleSet.hovered,
          fillAlpha: usesFireSectioning ? 0.07 : 0.5, // Slight transparency on hover
        };
      }
      this.styleSet = protanStyleSet;
    } else {
      this.styleSet = styleSet || null;
    }

    this.setStyle(SheetShapeStyleType.default);
  }

  /**
   * Convert gap from meters to sheet coordinates using calibration
   */
  private convertGapToSheetCoordinates(
    gapInMeters: number,
    calibration?: SheetScaleType
  ): number {
    if (!calibration) {
      // Fallback: assume 1 sheet unit = 0.01 meters (similar to previous behavior)
      // This gives gap=0.5m => 50 sheet units (similar to old gap=50)
      return gapInMeters * 100;
    }

    // Calculate the sheet distance between calibration points
    const dx = calibration.pointB.x - calibration.pointA.x;
    const dy = calibration.pointB.y - calibration.pointA.y;
    const sheetDistance = Math.sqrt(dx * dx + dy * dy);

    // Convert destinationWidth to meters based on destinationUnit
    let destinationWidthInMeters: number;
    switch (calibration.destinationUnit) {
      case CalibrationUnit.M:
        destinationWidthInMeters = calibration.destinationWidth;
        break;
      case CalibrationUnit.Mm:
        destinationWidthInMeters = calibration.destinationWidth / 1000;
        break;
      case CalibrationUnit.Feet:
        destinationWidthInMeters = calibration.destinationWidth * 0.3048;
        break;
      default:
        destinationWidthInMeters = calibration.destinationWidth;
    }

    // Scale factor: sheet units per meter
    const sheetUnitsPerMeter = sheetDistance / destinationWidthInMeters;

    return gapInMeters * sheetUnitsPerMeter;
  }

  /**
   * Create hatch pattern shapes for the polygon
   * @param fillColor - The polygon's fill color to use for hatch lines
   * @param calibration - Sheet calibration for converting meters to sheet coordinates
   */
  private createHatchPatternShapes(
    polygons: (MultiPolygon2Fragment['polygons'][number] | PolygonShapeType)[],
    pattern: HatchPatternConfig,
    convertPoints: boolean,
    fillColor: string,
    calibration?: SheetScaleType
  ) {
    try {
      // Helper to ensure ring is closed (first and last point are the same)
      const ensureRingClosed = (
        ring: [number, number][]
      ): [number, number][] => {
        if (ring.length < 3) return ring;
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
          return [...ring, first];
        }
        return ring;
      };

      // Convert gap from meters to sheet coordinates
      const gapInSheetCoords = this.convertGapToSheetCoordinates(
        pattern.gap,
        calibration
      );

      // Create a pattern config with the converted gap
      const patternWithConvertedGap: HatchPatternConfig = {
        ...pattern,
        gap: gapInSheetCoords,
      };

      // Convert polygons to MultiPolygon2 format for hatch generation
      const multiPolygon: MultiPolygon2 = {
        polygons: polygons.map((polygon) => ({
          exterior: ensureRingClosed(
            polygon.exterior.points.map((p) => [p.x, p.y] as [number, number])
          ),
          interiors: polygon.interiors.map((interior) =>
            ensureRingClosed(
              interior.points.map((p) => [p.x, p.y] as [number, number])
            )
          ),
        })),
      };

      // Generate hatch lines with converted gap
      const hatchLines = generateHatchLines(
        multiPolygon,
        patternWithConvertedGap
      );

      // Convert to Edit2D shapes - use fill color for hatch lines
      this.hatchShapes = createHatchShapes(
        hatchLines,
        (point) => {
          if (convertPoints) {
            return super.convertPointToRender(point);
          }
          return point;
        },
        {
          lineColor: fillColor,
          lineWidth: pattern.lineWidth || 0.02,
        }
      );
    } catch (error) {
      console.warn('Error creating hatch pattern shapes:', error);
      this.hatchShapes = [];
    }
  }

  private createProtanVisualizations(
    shapeModel: SheetShapeDeepFragment,
    externalShapes: SheetShapeDeepFragment[],
    calibration: SheetScaleType,
    sparkelProperties?: GetSparkelPropertiesForProjectQuery
  ) {
    const protanVisualizations = createProtanPolygonVisualizations(
      shapeModel,
      externalShapes,
      calibration,
      sparkelProperties,
      (point) => super.convertPointToRender(point)
    );

    this.protanShapes.fireSectioningShapes =
      protanVisualizations.fireSectioningShapes;
    this.protanShapes.cornerAreaShapes = protanVisualizations.cornerAreaShapes;
    this.protanShapes.randAreaShapes = protanVisualizations.randAreaShapes;
  }

  private _toSVG(
    polygon: MultiPolygon2Fragment['polygons'][number] | PolygonShapeType,
    convertPoints = true
  ): string {
    const exterior =
      'M ' +
      polygon.exterior.points
        .map((point) => {
          const converted = convertPoints
            ? super.convertPointToRender(point)
            : point;

          return `${converted.x},${converted.y}`;
        })
        .join(' L ');
    const interiors = polygon.interiors
      .map(
        ({ points }) =>
          'M ' +
          points
            .map((point) => {
              const converted = convertPoints
                ? super.convertPointToRender(point)
                : point;

              return `${converted.x},${converted.y}`;
            })
            .join(' L ')
      )
      .join(' ');

    return interiors?.length
      ? `<path d="${exterior} Z ${interiors} Z"/>`
      : `<path d="${exterior} Z"/>`;
  }

  draw() {
    super.draw();

    // Add hatch pattern shapes to the layer
    this.hatchShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.addShape(shape);
    });

    // Respect wind load zones toggle from localStorage (no global state)
    let showWindLoadZones = true;
    try {
      const stored = window?.localStorage?.getItem('show-wind-load-zones');
      showWindLoadZones =
        stored && stored !== 'undefined' ? JSON.parse(stored) : true;
    } catch {
      // default to true on parse/access errors
      showWindLoadZones = true;
    }

    // Explicitly add Protan visualization shapes to the Edit2D layer
    if (showWindLoadZones) {
      this.protanShapes.randAreaShapes.forEach((shape) => {
        this.edit2d.defaultContext.layer.addShape(shape);
      });
      this.protanShapes.cornerAreaShapes.forEach((shape) => {
        this.edit2d.defaultContext.layer.addShape(shape);
      });
    }
    this.protanShapes.fireSectioningShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.addShape(shape);
    });
  }

  /**
   * Listen for runtime toggle events to add/remove wind load zones without full redraw
   */
  public attachWindLoadZonesListener() {
    const handler = (e: Event) => {
      const enabled = (e as CustomEvent).detail?.enabled;
      if (enabled === undefined) return;
      if (enabled) {
        this.protanShapes.randAreaShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.addShape(shape);
        });
        this.protanShapes.cornerAreaShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.addShape(shape);
        });
        // Ensure fire sectioning shapes are always on top by re-adding them last
        this.protanShapes.fireSectioningShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.removeShape(shape);
        });
        this.protanShapes.fireSectioningShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.addShape(shape);
        });
      } else {
        this.protanShapes.randAreaShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.removeShape(shape);
        });
        this.protanShapes.cornerAreaShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.removeShape(shape);
        });
      }
      this.viewer.impl.invalidate(true, true, true);
    };

    window.addEventListener('SheetsV2:windLoadZonesToggle', handler);

    // Store a remover on the instance so we can clean up later
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this._removeWindToggleHandler = () =>
      window.removeEventListener('SheetsV2:windLoadZonesToggle', handler);
  }

  remove() {
    super.remove();

    // Remove hatch pattern shapes from the layer
    this.hatchShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.removeShape(shape);
    });

    // Explicitly remove all Protan visualization shapes from the Edit2D layer
    this.protanShapes.randAreaShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.removeShape(shape);
    });
    this.protanShapes.cornerAreaShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.removeShape(shape);
    });
    this.protanShapes.fireSectioningShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.removeShape(shape);
    });
  }
}
